import { PaginatedList } from "@taunggyi-plus/design-system";
import { useFeedback } from "@taunggyi-plus/auth";
import { ActionIcon, RECORDS_PER_PAGE } from "@taunggyi-plus/design-system";
import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@taunggyi-plus/auth";
import { Card, ManagementListToolbar } from "@taunggyi-plus/design-system";
import { queueInfo, serviceLabels } from "./dashboard-model";

type QueuePage = {
  total: number;
  statuses: string[];
  items: {
    id: string;
    customer: string;
    provider: string;
    contact: string;
    detail: string;
    status: string;
    created_at: string;
    overdue: boolean;
  }[];
};
export function DashboardQueue({ service, queue }: { service: string; queue: string }) {
  const { client } = useAuth();
  const [page, setPage] = useState<QueuePage | null>(null);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all");
  const [overdue, setOverdue] = useState(
    new URLSearchParams(window.location.search).get("overdue") === "1",
  );
  const [offset, setOffset] = useState(0);
  const [error, setError] = useFeedback("", "error");
  const [busy, setBusy] = useState(false);
  const sequence = useRef({ value: 0 });
  const info = queueInfo(service, queue);
  const load = useCallback(async () => {
    if (!client) return;
    const current = ++sequence.current.value;
    setBusy(true);
    try {
      const result = await client.rpc("admin_dashboard_queue_filtered", {
        target_service: service,
        target_queue: queue,
        target_offset: offset,
        target_query: query,
        target_status: status,
        target_overdue: overdue,
      });
      if (sequence.current.value !== current) return;
      if (result.error) throw new Error(result.error.message);
      setPage(result.data as QueuePage);
      setError("");
    } catch {
      if (sequence.current.value === current)
        setError("This queue could not be loaded. Please refresh.");
    } finally {
      if (sequence.current.value === current) setBusy(false);
    }
  }, [client, service, queue, offset, query, status, overdue, setError]);
  useEffect(() => {
    const lifecycle = sequence.current;
    const timer = window.setTimeout(() => void load(), 0);
    return () => {
      ++lifecycle.value;
      window.clearTimeout(timer);
    };
  }, [load]);
  return (
    <section className="admin-dashboard-queue">
      <a href="/more" className="more-back-link">
        ← More
      </a>
      <div className="admin-dashboard-context">
        <div>
          <p className="eyebrow">{serviceLabels[service]}</p>
          <h2>{info?.title ?? "Service work"}</h2>
        </div>
        <button
          className="toolbar-icon-button"
          aria-label="Refresh service work"
          title="Refresh service work"
          type="button"
          disabled={busy}
          onClick={() => void load()}
        >
          <ActionIcon action="refresh" />
        </button>
      </div>

      <ManagementListToolbar
        query={query}
        onQueryChange={(value) => {
          setQuery(value);
          setOffset(0);
        }}
        searchLabel="Search customer or provider"
        resultCount={page?.total ?? 0}
        filters={[
          {
            label: "Status",
            value: status,
            onChange: (value) => {
              setStatus(value);
              setOffset(0);
            },
            options: [
              { value: "all", label: "All statuses" },
              ...(page?.statuses ?? []).map((value) => ({
                value,
                label: value.replaceAll("_", " "),
              })),
            ],
          },
          {
            label: "Waiting time",
            value: overdue ? "overdue" : "all",
            onChange: (value) => {
              setOverdue(value === "overdue");
              setOffset(0);
            },
            options: [
              { value: "all", label: "All waiting times" },
              { value: "overdue", label: "Overdue only" },
            ],
          },
        ]}
        onClear={() => {
          setQuery("");
          setStatus("all");
          setOverdue(false);
          setOffset(0);
        }}
      />
      {error && <p role="alert">{error}</p>}
      {busy && <p role="status">Loading queue…</p>}
      {!busy && !error && page && (
        <>
          <p>{page.total.toLocaleString()} work items · Oldest first</p>
          <div className="admin-work-grid">
            <PaginatedList
              pagination="external"
              items={page.items}
              label="Work queue"
              renderItem={(item) => (
                <Card className="admin-queue-item" key={item.id}>
                  <div className="admin-service-title">
                    <h3>{item.customer || "Customer"}</h3>
                    <span className="admin-dashboard-badge">
                      {item.status.replaceAll("_", " ")}
                    </span>
                  </div>
                  {item.overdue && <span className="overdue-indicator">Overdue</span>}
                  <p>{item.detail}</p>
                  <strong>{item.provider}</strong>
                  {item.contact && (
                    <a href={`tel:${item.contact.replace(/[^+\d]/g, "")}`}>
                      Call provider: {item.contact}
                    </a>
                  )}
                  <small>
                    {new Date(item.created_at).toLocaleString("en-GB", { timeZone: "Asia/Yangon" })}{" "}
                    · Myanmar time
                  </small>
                  <small>Reference: {item.id}</small>
                </Card>
              )}
            />
          </div>
          {!page.total && <Card className="empty-state">No work outstanding in this queue.</Card>}
          {page.total > 0 && (
            <nav className="admin-dashboard-pagination" aria-label="Queue pages">
              <button
                className="button button--secondary"
                type="button"
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - RECORDS_PER_PAGE))}
              >
                Previous
              </button>
              <span>
                Page {Math.floor(offset / RECORDS_PER_PAGE) + 1} of{" "}
                {Math.max(1, Math.ceil(page.total / RECORDS_PER_PAGE))}
              </span>
              <button
                className="button button--secondary"
                type="button"
                disabled={offset + RECORDS_PER_PAGE >= page.total}
                onClick={() => setOffset(offset + RECORDS_PER_PAGE)}
              >
                Next
              </button>
            </nav>
          )}
        </>
      )}
    </section>
  );
}
